import React from 'react';
import Checkbox from './Checkbox';
import NumericStepper from './NumericStepper';

interface ConfigurationPanelProps {
    woodenDunnageEnabled: boolean;
    setWoodenDunnageEnabled: (enabled: boolean) => void;
    stopperEnabled: boolean;
    setStopperEnabled: (enabled: boolean) => void;
    loopLashingEnabled: boolean;
    setLoopLashingEnabled: (enabled: boolean) => void;
    selectedLoopRope: string | null;
    setSelectedLoopRope: (rope: string | null) => void;
    isVesselSelected: boolean;
    calculationMode: 'optimizer' | 'calculator';
    sortedLoopHalatOptions: any[];
    manualLashings: any;
    handleManualLashingChange: (direction: any, field: any, value: any, index?: number) => void;
    t: (key: string) => string;
    loopLashingDisabled?: boolean;
    isAdmin: boolean;
    transportUnit: string;
    lashingDisplayMode?: 'msl' | 'cordstrap';
}

const ConfigurationPanel: React.FC<ConfigurationPanelProps> = ({
    woodenDunnageEnabled,
    setWoodenDunnageEnabled,
    stopperEnabled,
    setStopperEnabled,
    loopLashingEnabled,
    setLoopLashingEnabled,
    selectedLoopRope,
    setSelectedLoopRope,
    isVesselSelected,
    calculationMode,
    sortedLoopHalatOptions,
    manualLashings,
    handleManualLashingChange,
    t,
    isAdmin,
    transportUnit,
    lashingDisplayMode = 'msl',
}) => {

    const handleLoopLashingChange = (enabled: boolean) => {
        setLoopLashingEnabled(enabled);
        if (!enabled) {
            setSelectedLoopRope(null);
        }
    };

    return (
        <div className="bg-white dark:bg-[#202c42] rounded-xl border border-slate-200 dark:border-slate-400/20 shadow-sm overflow-hidden transition-all duration-300 dark:backdrop-blur-md">
            <div className="p-3 space-y-2">
                <div className="space-y-1">
                    {/* If transport unit is vessel: show only Stopper */}
                    {isVesselSelected && (
                        <Checkbox
                            label={t('stopper')}
                            checked={stopperEnabled}
                            onChange={setStopperEnabled}
                            disabled={false}
                        />
                    )}

                    {/* If transport unit is not vessel (or is mafi): show only Wooden Dunnage */}
                    {(!isVesselSelected || transportUnit === 'mafi') && (
                        <Checkbox
                            label={t('wooden_dunnage')}
                            checked={woodenDunnageEnabled}
                            onChange={setWoodenDunnageEnabled}
                            disabled={false}
                            compact
                        />
                    )}
                </div>

                <div className="mt-1 border-t border-slate-100 dark:border-white/5 pt-2">
                    <div className="space-y-1.5">
                        <Checkbox
                            label={t('loop_lashing')}
                            checked={loopLashingEnabled}
                            onChange={handleLoopLashingChange}
                            compact
                        />
                        {loopLashingEnabled && (
                            <div className="mt-1.5 space-y-1.5 pl-6 animate-in fade-in slide-in-from-top-1 duration-200">
                                {calculationMode === 'optimizer' ? (
                                    <select
                                        value={selectedLoopRope || ''}
                                        onChange={(e) => setSelectedLoopRope(e.target.value || null)}
                                        className="w-full px-2 py-1 bg-slate-50 dark:bg-[#26344d] border border-slate-200 dark:border-slate-400/40 rounded-md text-slate-800 dark:text-slate-50 focus:outline-none focus:ring-2 focus:ring-orange-400/30 focus:border-orange-400 text-xs font-medium transition-all backdrop-blur-sm"
                                    >
                                        <option value="">{t('select')}</option>
                                        {sortedLoopHalatOptions.map(h => (
                                            <option key={h.optionKey} value={h.optionKey}>
                                                {lashingDisplayMode === 'cordstrap' && h.cordstrap_name
                                                    ? h.cordstrap_name
                                                    : (h.msl_tur || h.name)} {isAdmin && `(L_Lashing_MSL: ${h.l_lashing_msl} kN)`}
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    <div className="flex items-center gap-1.5 max-[480px]:flex-col max-[480px]:items-stretch max-[480px]:gap-2">
                                        <select
                                            value={manualLashings.loop.ropeKey || ''}
                                            onChange={(e) => handleManualLashingChange('loop', 'ropeKey', e.target.value)}
                                            className="flex-1 min-w-0 px-2 py-1 bg-slate-50 dark:bg-[#26344d] border border-slate-200 dark:border-slate-400/40 rounded-md text-slate-800 dark:text-slate-50 focus:outline-none focus:ring-2 focus:ring-orange-400/30 focus:border-orange-400 text-xs font-medium h-7 transition-all backdrop-blur-sm max-[480px]:w-full max-[480px]:h-8"
                                        >
                                            <option value="">{t('select')}</option>
                                            {sortedLoopHalatOptions.map(h => (
                                                <option key={h.optionKey} value={h.optionKey}>
                                                    {lashingDisplayMode === 'cordstrap' && h.cordstrap_name
                                                        ? h.cordstrap_name
                                                        : (h.msl_tur || h.name)} {isAdmin && `(L_Lashing_MSL: ${h.l_lashing_msl} kN)`}
                                                </option>
                                            ))}
                                        </select>
                                        <div className="shrink-0 flex items-center bg-slate-50 dark:bg-white/5 p-0.5 rounded-md border border-slate-100 dark:border-white/10 max-[480px]:w-full" title={t('loop_lashing_quantity_per_side')}>
                                            <NumericStepper
                                                label=""
                                                value={manualLashings.loop.count}
                                                min={0}
                                                step={1}
                                                disableWheel={true}
                                                compact
                                                onChange={(value) => handleManualLashingChange('loop', 'count', value)}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConfigurationPanel;
